import { addResizeListener, removeResizeListener } from 'element-ui/src/utils/resize-event';
import { debounce } from 'lodash-es';

export default {
  data() {
    return {
      tableHeightRow: {
        height: 200,
        bottomOffset: 85,
        minHeight: 200
      },
      _instanceId: `table_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      _isCalculating: false,
      _resizeHandler: null,
      _visibilityObserver: null
    };
  },

  created() {
    // 使用 lodash 的 debounce，替换自己的实现
    this._resizeHandler = debounce(this.doResize, 200);
    this._visibilityHandler = debounce(this.onVisibleChange, 100);
  },

  mounted() {
    // 初始化高度
    this.$nextTick(() => {
      this.doResize();
    });

    // 监听窗口变化
    addResizeListener(document.body, this._resizeHandler);

    // 监听可见性变化
    this.setupVisibilityObserver();
  },

  activated() {
    // keep-alive 激活时重新计算
    this.$nextTick(() => {
      this.doResize();
    });
  },

  beforeDestroy() {
    removeResizeListener(document.body, this._resizeHandler);
    this.cleanupVisibilityObserver();
  },

  methods: {
    /**
     * 核心计算方法
     */
    doResize() {
      if (this._isCalculating) return;

      this._isCalculating = true;
      requestAnimationFrame(() => {
        const el = this.getTableElement();
        if (el) {
          this.calculateHeight(el);
        }
        this._isCalculating = false;
      });
    },

    /**
     * 获取表格元素 - 支持父子组件
     */
    getTableElement() {
      // 策略1: 优先使用 ref（最可靠）
      if (this.$refs?.table) {
        return this.$refs.table?.$el || this.$refs.table;
      }

      // 策略2: 通过 class 查找，确保属于当前组件
      if (this.$el) {
        const elInComponent = this.$el.querySelector('.table-height');
        if (elInComponent) return elInComponent;
      }

      // 策略3: 全局查找，验证归属
      const globalEl = document.querySelector('.table-height');
      if (globalEl && this.$el?.contains(globalEl)) {
        return globalEl;
      }

      return null;
    },

    /**
     * 计算高度
     */
    calculateHeight(el) {
      try {
        const rect = el.getBoundingClientRect();
        const height = window.innerHeight - rect.top - this.tableHeightRow.bottomOffset;
        const finalHeight = Math.max(height, this.tableHeightRow.minHeight);

        // 只在高度变化时更新，避免重渲染
        if (this.tableHeightRow.height !== finalHeight) {
          this.tableHeightRow.height = finalHeight;
        }
      } catch (error) {
        console.warn(`[${this._instanceId}] 计算高度失败:`, error);
      }
    },

    /**
     * 可见性监听设置
     */
    setupVisibilityObserver() {
      if (!window.IntersectionObserver || !this.$el) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this._visibilityHandler();
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(this.$el);
      this._visibilityObserver = observer;
    },

    /**
     * 清理可见性监听
     */
    cleanupVisibilityObserver() {
      if (this._visibilityObserver) {
        this._visibilityObserver.disconnect();
        this._visibilityObserver = null;
      }
    },

    /**
     * 可见性变化回调
     */
    onVisibleChange() {
      this.$nextTick(() => {
        this.doResize();
      });
    },

    /**
     * 手动刷新高度
     */
    refreshTableHeight() {
      this.doResize();
    }
  }
};
